import React, { useState, useEffect } from "react";
import axios from "axios";
import { BookOpen, Target, BarChart3, Users } from "lucide-react";

const WritingGuidelines = ({ currentScenarioData, writingAttempt, isPlagiarized }) => {
  const [rubrics, setRubrics] = useState([]);

  // ✅ Gọi API rubric riêng
  useEffect(() => {
    const fetchRubric = async () => {
      if (!currentScenarioData?.id) return;
      try {
        const res = await axios.get(`/scenarios/writing/${currentScenarioData.id}/rubric/detail`);
        if (Array.isArray(res.data)) {
          setRubrics(res.data);
        }
      } catch (error) {
        console.error("Error fetching rubric:", error);
      }
    };
    fetchRubric();
  }, [currentScenarioData?.id]);

  // ✅ Get writing task instruction
  const getWritingTask = () => {
    if (!currentScenarioData) return { content: "" };
    return {
      title: currentScenarioData.guidelines_title || "Writing Guidelines",
      content:
        currentScenarioData.guidelines_description ||
        currentScenarioData.writing_instruction ||
        "",
    };
  };

  // ✅ Word count from API
  const getWordCountRequirement = () => {
    if (!currentScenarioData) return "150-180 words";
    return (
      currentScenarioData.word_count_range ||
      currentScenarioData.word_count ||
      "150-180 words"
    );
  };

  // ✅ Render formatted instruction
  const renderInstructionContent = (instructionData) => {
    if (!instructionData) return null;
    const title = instructionData.title || null;
    const content = instructionData.content || "";

    return (
      <div className="bg-white rounded-md p-4 border border-blue-100">
        {title && (
          <h5 className="font-semibold text-gray-800 text-base mb-3 text-center uppercase tracking-wide">
            {title}
          </h5>
        )}
        <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
          {content}
        </p>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* ✅ Instruction Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
        <div className="flex items-center space-x-2 mb-3">
          <Target className="w-4 h-4 text-blue-600" />
          <h4 className="font-semibold text-blue-800 text-sm">Instruction</h4>
        </div>

        {renderInstructionContent(getWritingTask())}

        {/* ✅ Outline Instruction */}
        {currentScenarioData?.outline_instruction && (
          <div className="bg-blue-50 border-l-4 border-blue-300 p-3 rounded-md mt-3">
            <h5 className="font-semibold text-sm text-blue-800 mb-1">Outline Instruction</h5>
            <p className="text-gray-700 text-xs leading-relaxed">
              {currentScenarioData.outline_instruction}
            </p>
          </div>
        )}
      </div>

      {/* ✅ Writing Rubric Table */}
      {(rubrics.length > 0 ||
        (currentScenarioData?.rubric_json && Array.isArray(currentScenarioData.rubric_json))) && (
          <div className="bg-white rounded-md border border-blue-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-100 to-indigo-100 px-3 py-2 border-b border-blue-200">
              <h5 className="font-semibold text-gray-800 text-sm flex items-center">
                <BarChart3 className="w-4 h-4 text-blue-600 mr-2" />
                Writing Rubric
              </h5>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left p-2 font-semibold text-gray-700 min-w-[200px]">
                      Indicators
                    </th>
                    <th className="text-center p-2 font-semibold text-gray-700 bg-yellow-50 border-l border-gray-200 min-w-[120px]">
                      B1
                    </th>
                    <th className="text-center p-2 font-semibold text-gray-700 bg-orange-50 border-l border-gray-200 min-w-[120px]">
                      B2
                    </th>
                    <th className="text-center p-2 font-semibold text-gray-700 bg-green-50 border-l border-gray-200 min-w-[120px]">
                      C1
                    </th>
                    <th className="text-center p-2 font-semibold text-gray-700 bg-blue-50 border-l border-gray-200 min-w-[120px]">
                      C2
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(rubrics.length > 0
                    ? rubrics
                    : currentScenarioData.rubric_json
                  ).map((criterion, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-25">
                      <td className="p-2 font-medium text-gray-800 border-r border-gray-100 align-top">
                        {index + 1}.{" "}
                        {criterion.indicator ||
                          criterion.criteria ||
                          "—"}
                      </td>
                      <td className="p-2 text-gray-600 bg-yellow-25 border-l border-r border-gray-100 align-top text-xs leading-relaxed">
                        {criterion.B1 || criterion.b1 || "—"}
                      </td>
                      <td className="p-2 text-gray-600 bg-orange-25 border-r border-gray-100 align-top text-xs leading-relaxed">
                        {criterion.B2 || criterion.b2 || "—"}
                      </td>
                      <td className="p-2 text-gray-600 bg-green-25 border-r border-gray-100 align-top text-xs leading-relaxed">
                        {criterion.C1 || criterion.c1 || "—"}
                      </td>
                      <td className="p-2 text-gray-600 bg-blue-25 border-r border-gray-100 align-top text-xs leading-relaxed">
                        {criterion.C2 || criterion.c2 || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {/* ✅ Writing Process */}
      {currentScenarioData?.writing_process && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center space-x-2 mb-3">
            <Users className="w-4 h-4 text-green-600" />
            <h4 className="font-semibold text-green-800 text-sm">
              Writing Process Guide
            </h4>
          </div>
          <p className="text-xs text-gray-700 whitespace-pre-line">
            {currentScenarioData.writing_process}
          </p>
        </div>
      )}

      {/* ✅ Self Assessment */}
      {currentScenarioData?.self_assessment && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center space-x-2 mb-3">
            <BarChart3 className="w-4 h-4 text-purple-600" />
            <h4 className="font-semibold text-purple-800 text-sm">
              Self-Assessment
            </h4>
          </div>
          <p className="text-xs text-gray-700 whitespace-pre-line">
            {currentScenarioData.self_assessment}
          </p>
        </div>
      )}

      {/* ✅ Quick Reference */}
      <div className="bg-gray-800 text-white rounded-lg p-4 mt-6">
        <h5 className="font-semibold text-sm mb-2 flex items-center">
          <BookOpen className="w-4 h-4 mr-2" />
          Quick Reference
        </h5>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-gray-300">Word Count:</span>
            <span className="ml-1 font-medium">{getWordCountRequirement()}</span>
          </div>
          <div>
            <span className="text-gray-300">Format:</span>
            <span className="ml-1 font-medium">Academic</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WritingGuidelines;
